import { makeAutoObservable, type IReactionDisposer, reaction } from 'mobx'
import { nanoid } from 'nanoid'
import { AxiosError } from 'axios'
import { isEmail, logger } from 'shared/lib'
import { AuthApi } from 'entities/Auth/api'
import { type ReCaptchaStore } from 'entities/Auth'
import { getCaptchaErrorMessage } from 'entities/Auth/lib/captchaErrors'
import { resolveForgotOutcome } from 'pages/auth/pages/forgot/ui/ForgotPassword/lib/resolveForgotOutcome'

const GENERIC_ERROR = 'Something went wrong. Please try again.'

export class ForgotPasswordStore {
  private _email = ''
  private _loading = false
  private _success = false

  // Email validation
  private _disposeEmailValidation: IReactionDisposer | null = null
  private _disposeEmailValidationTrigger: IReactionDisposer | null = null
  private _formError: string | null = null
  private _emailValidationError: string | null = null
  private _emailValidationTrigger: string | null = null

  constructor(private _storeReCaptchaStore: ReCaptchaStore, initialEmail?: string) {
    if (initialEmail) {
      this._email = initialEmail
    }

    makeAutoObservable(this)
    this.reactionEmailValidation()
    this.reactionEmailValidationTrigger()
  }

  get isValidEmail() {
    return isEmail(this._email)
  }

  get hasEmail() {
    return !!this.email
  }

  get email() {
    return this._email
  }

  get loading() {
    return this._loading
  }

  get formError() {
    return this._formError
  }

  get success() {
    return this._success
  }

  get emailValidationError() {
    return this._emailValidationError
  }

  sendResetPasswordLink = async () => {
    if (!this.isValidEmail) return
    this.clearErrors()
    try {
      this.setLoading(true)

      await this._storeReCaptchaStore.setExecuteRecaptcha('auth_forgot')

      await AuthApi.forgotPassword(
        {
          email: this.email,
          ...this._storeReCaptchaStore.params,
        },
        {
          reCaptchaStore: this._storeReCaptchaStore,
        }
      )
      this._success = true
    } catch (e) {
      logger.error(e)
      if (e instanceof AxiosError) {
        // A visual challenge is pending: the reCAPTCHA layout takes over and the interceptor
        // retries, so showing anything here would be wrong.
        if (this._storeReCaptchaStore.checkServerError(e.errors?.getError('message') ?? '')) return

        if (resolveForgotOutcome(e) === 'neutral') {
          this._success = true

          return
        }

        this._formError = getCaptchaErrorMessage(e) ?? GENERIC_ERROR

        return
      }

      this._formError = GENERIC_ERROR
    } finally {
      this.setLoading(false)
    }
  }

  clearErrors = () => {
    if (this._formError) this._formError = ''
    if (this._emailValidationError) this._emailValidationError = ''
  }

  setLoading = (value: boolean) => {
    this._loading = value
  }

  setEmail = (value: string) => {
    if (this.isValidEmail) this.clearErrors()
    this._email = value.trim()
  }

  triggerEmailValidation = () => {
    this._emailValidationTrigger = nanoid()
  }

  reactionEmailValidation = () => {
    this._disposeEmailValidation?.()
    this._disposeEmailValidation = reaction(
      () => [this._emailValidationTrigger, this.isValidEmail],
      (isValid) => {
        if (isValid) this.clearErrors()
      },
      {
        fireImmediately: true,
      }
    )
  }

  reactionEmailValidationTrigger = () => {
    this._disposeEmailValidationTrigger?.()
    this._disposeEmailValidationTrigger = reaction(
      () => this._emailValidationTrigger,
      (value) => {
        this._emailValidationError =
          value && !this.isValidEmail
            ? this.hasEmail
              ? 'Please enter a valid email address.'
              : 'Please enter email address.'
            : null
        if (value && this.isValidEmail) {
          this.clearErrors()
          if (document.activeElement instanceof HTMLElement) document.activeElement.blur()
          this.sendResetPasswordLink()
        }
      },
      {
        fireImmediately: true,
      }
    )
  }
}
